import { useState, useEffect, useRef, useMemo } from 'react';
import { FaCheckCircle, FaTimesCircle, FaRegSquare, FaSpinner } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import { SmilePlus, AlertTriangle, Beer, AlertCircle, LogOut, Clock } from 'lucide-react';
import { RESTAURANT_BACKEND_HOST } from './RestaurantBackend';
import type { Task } from './AudioStreamer';
import { Summary } from '../../types/Summary';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../contexts/AuthContext';
import { normalizeRole } from '../auth/AdminRoute';
import { config } from '../../config/environment';
import { parseEmojiString } from '../../utils/metricUtils';
import { fetchApi } from '../../utils/api';
import {
  buildStatusReadout,
  type RecordingStatus,
  type StatusTone,
} from '../../utils/liveServerStatus';

interface ServerInfo {
  id: string;
  name: string;
  role: string;
  tenant_id?: string;
  location?: string;
  /**
   * Server-pushed live recording status (feat-080). Optional & additive: older
   * backends omit it and the tile degrades to name + location (AC7). React only
   * renders this — it never classifies (AC6).
   */
  recording_status?: RecordingStatus;
}

interface EvaluationResponse {
  tasks: Task[];
}

interface TranscriptSegment {
  speaker: number;
  text: string;
}

interface FulfilledMetric {
  key: string;
  label: string;
  emoji?: string;
}

interface TranscriptSession {
  id: number;
  notification?: {
    text: string;
    positive: boolean;
    orderedItems?: string[];
    interaction_type?: string;
    fulfilled_metrics?: FulfilledMetric[];
    // Populated only when interaction_type === 'phrase_rule'
    phrase?: string;
    polarity?: 'positive' | 'negative';
    matched_text?: string;
    evidence?: string;
  };
  recordingEvent?: {
    type: 'resume_recording' | 'pause_recording';
    beaconName?: string;
    beaconPower?: number;
    userResumed?: boolean;
    userPaused?: boolean;
  };
  transcriptParts: TranscriptSegment[];
  interimTranscript: string;
}

/**
 * feat-080 live status chip on an Active Server tile. Tiles are coral, so tones
 * are chosen to stay legible AND distinct against that background:
 *  - healthy (recording/connecting): calm white chip, green dot.
 *  - paused: emphasized solid-gold chip — the forgot-to-unpause headline.
 *  - stalled: white chip + red text + alert icon + pulse. (Solid red would blend
 *    into the coral tile, so an alarming-but-legible white treatment is used.)
 * Renders only the server-pushed state + a cosmetic count-up — no classification.
 */
const TONE_CLASSES: Record<StatusTone, string> = {
  healthy: 'bg-white/90 text-success',
  paused: 'bg-gold text-ink font-semibold shadow-sm',
  stalled: 'bg-white text-[var(--destructive)] font-semibold',
};

export const ServerStatusChip = ({
  status,
  serverNowMs,
}: {
  status: RecordingStatus;
  serverNowMs: number;
}) => {
  const readout = buildStatusReadout(status.state, status.since, serverNowMs);
  const isConnecting = status.state === 'connecting';
  return (
    <div
      role="status"
      className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs leading-none ${
        TONE_CLASSES[readout.tone]
      } ${readout.alarming ? 'animate-pulse' : ''}`}
    >
      {readout.alarming ? (
        <AlertTriangle className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
      ) : (
        <span
          aria-hidden="true"
          className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
            readout.tone === 'paused' ? 'bg-ink' : 'bg-success'
          } ${isConnecting ? 'animate-pulse' : ''}`}
        />
      )}
      <span className="whitespace-nowrap">{readout.label}</span>
    </div>
  );
};

const RestaurantAdmin = () => {
  const { user } = useAuth();
  // feat-274: POST /admin/force-quit-all-active-servers now requires admin on the
  // backend. This route is NOT AdminRoute-gated and should not be — the rest of
  // the page works for non-admins — so gate the action, not the page. Same
  // normalizeRole the route guard uses, so the two cannot drift.
  const isAdmin = normalizeRole(user?.managementUser?.role) === 'Admin';
  const [activeServers, setActiveServers] = useState<ServerInfo[]>([]);
  // feat-294-filter-active-servers: "Hide live clients" (Demo mode) shows only servers whose tenant
  // has `is_live === false` (tenant.is_live, GET /api/tenant). ON on every load,
  // never persisted. Fails CLOSED: a server whose tenant is unknown (no
  // tenant_id, missing from the map, fetch failed or still loading) is hidden,
  // so a live client's crew never flashes up mid-demo. The admin WebSocket
  // carries tenant_id but no tenant flags, hence the client-side join.
  const [demoMode, setDemoMode] = useState(true);
  const [tenantIsLive, setTenantIsLive] = useState<Map<string, boolean>>(new Map());
  const isVisible = (s: ServerInfo) =>
    !demoMode || (s.tenant_id != null && tenantIsLive.get(String(s.tenant_id)) === false);
  // Read by the WebSocket handler, whose closure would otherwise see stale state.
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TranscriptSession[]>([
    { id: 0, transcriptParts: [], interimTranscript: '' }
  ]);
  const [evaluation, setEvaluation] = useState<Task[]>([]);
  const [isFinalEvaluation, setIsFinalEvaluation] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [detailedSummary, setDetailedSummary] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const websocketRef = useRef<WebSocket | null>(null);

  // feat-080 live status count-up. `clockOffsetMs` = clientNow − server `as_of`,
  // recomputed on each server_update so the per-second count-up tracks server
  // wall-clock regardless of device-clock skew (ADR 0001 §D4). `nowMs` ticks
  // once per second via a single shared interval (not per-tile timers).
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Build WebSocket URL with auth token if available
  const getWebSocketUrl = () => {
    const wsProtocol = (config.environment === 'fake' || config.environment === 'local') ? 'ws' : 'wss';
    let url = `${wsProtocol}://${RESTAURANT_BACKEND_HOST}/ws/admin`;
    // Add token as query parameter if available
    if (!config.useDummyData && user?.accessToken) {
      url += `?token=${encodeURIComponent(user.accessToken)}`;
    }
    return url;
  };

  const connectWebSocket = () => {
    const websocketUrl = getWebSocketUrl();
    const ws = new WebSocket(websocketUrl);
    websocketRef.current = ws;

      ws.onopen = () => {
        const timestamp = new Date().toISOString();
        console.log(`[RestaurantAdmin] [${timestamp}] Admin WebSocket connected`);
        // Note: Re-establishing watch is handled by the useEffect hook below
        // which watches selectedServer changes and ensures watching is set up
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const timestamp = new Date().toISOString();
        console.debug(`[RestaurantAdmin] [${timestamp}] Received admin message:`, data);

        switch (data.type) {
          case 'server_update':
            // feat-080: de-skew the cosmetic count-up against server time.
            // offset = clientNow − as_of, computed once per message (ADR §D4).
            // Absent as_of (older backend) → offset 0, falls back to local now.
            if (typeof data.data.as_of === 'string') {
              const asOfMs = Date.parse(data.data.as_of);
              if (!Number.isNaN(asOfMs)) {
                setClockOffsetMs(Date.now() - asOfMs);
              }
            }
            const servers = data.data.active_servers || [];
            console.log(`[RestaurantAdmin] [${timestamp}] Received ${servers.length} servers from backend`);
            if (servers.length > 0) {
              console.log(`[RestaurantAdmin] [${timestamp}] Server details:`, servers.map((s: ServerInfo) => ({
                id: s.id,
                name: s.name,
                tenant_id: s.tenant_id
              })));
            } else {
              console.warn(`[RestaurantAdmin] [${timestamp}] WARNING: Received empty server list from backend!`);
            }
            setActiveServers(servers);
          setSelectedServer(currentSelectedServer => {
            let newSelectedServer = currentSelectedServer;
            if (currentSelectedServer && !data.data.active_servers.some((server: ServerInfo) => server.id === currentSelectedServer)) {
              setEvaluation([]);
              setIsFinalEvaluation(false);
              setSummary(null);
              setIsProcessing(false);
              newSelectedServer = null;
              clearAllSessions();
            }
            // feat-294-filter-active-servers: auto-select only among servers Demo mode shows.
            const selectable = data.data.active_servers.filter((s: ServerInfo) => isVisibleRef.current(s));
            if (newSelectedServer === null && selectable.length === 1) {
              const serverId = selectable[0].id;
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({
                  type: 'start_watching',
                  server_id: serverId
                }));
              }
              newSelectedServer = serverId;
            }
            return newSelectedServer;
          });
          break;
        case 'transcript_interim':
          if (typeof data.text === 'string') {
            setSessions(prevSessions => {
              const lastSession = prevSessions[prevSessions.length - 1];
              return [
                ...prevSessions.slice(0, -1),
                { ...lastSession, interimTranscript: data.text }
              ];
            });
          }
          break;
        case 'transcript_final':
          if (data.segments) {
            setSessions(prevSessions => {
              const lastSession = prevSessions[prevSessions.length - 1];
              const existingSegments = lastSession.transcriptParts;
              const combinedSegments = [...existingSegments, ...data.segments];

              return [
                ...prevSessions.slice(0, -1),
                {
                  ...lastSession,
                  transcriptParts: mergeConsecutiveSpeakers(combinedSegments),
                  interimTranscript: ''
                }
              ];
            });
          }
          break;
        case 'notification':
          setSessions(prevSessions => [
            ...prevSessions,
            {
              id: prevSessions.length,
              notification: {
                text: data.text,
                positive: data.positive,
                orderedItems: data.orderedItems,
                interaction_type: data.interaction_type,
                fulfilled_metrics: data.fulfilled_metrics,
                phrase: data.phrase,
                polarity: data.polarity,
                matched_text: data.matched_text,
                evidence: data.evidence,
              },
              transcriptParts: [],
              interimTranscript: ''
            }
          ]);
          break;
        case 'evaluation':
          if (Array.isArray(data.data.tasks)) {
            setEvaluation(data.data.tasks);
          }
          break;
        case 'evaluation_final':
          if (Array.isArray(data.data.tasks)) {
            setIsFinalEvaluation(true);
            setEvaluation(data.data.tasks);
            setSummary(data.summary || null);
            setDetailedSummary(data.detailedSummary || null);
            setIsProcessing(false);
          }
          break;
        case 'pause_recording':
        case 'resume_recording':
          setSessions(prevSessions => [
            ...prevSessions,
            {
              id: prevSessions.length,
              recordingEvent: {
                type: data.type,
                beaconName: data.beaconName,
                beaconPower: data.beaconPower,
                userPaused: data.userPaused,
                userResumed: data.userResumed
              },
              transcriptParts: [],
              interimTranscript: ''
            }
          ]);
          break;
      }
    };

    ws.onclose = (event) => {
      const timestamp = new Date().toISOString();
      console.log(`[RestaurantAdmin] [${timestamp}] Admin WebSocket disconnected. Code:`, event.code, 'Reason:', event.reason);
      // Only reconnect if it wasn't a normal closure (code 1000) or if we're still mounted
      if (event.code !== 1000) {
        console.log(`[RestaurantAdmin] [${timestamp}] Admin WebSocket disconnected. Reconnecting...`);
        setTimeout(connectWebSocket, 1000);
      }
    };

    ws.onerror = (error) => {
      console.error('Admin WebSocket error:', error);
    };
  };

  useEffect(() => {
    // Only connect if user is available (for token)
    if (config.useDummyData || user) {
      connectWebSocket();
    }

    return () => {
      // Cleanup on unmount
      if (websocketRef.current) {
        websocketRef.current.close(1000, 'Component unmounting');
        websocketRef.current = null;
      }
    };
  }, [user]); // Reconnect when user changes (to get new token)

  // Re-establish watching when selectedServer changes and WebSocket is connected
  useEffect(() => {
    if (selectedServer && websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      console.log(`[RestaurantAdmin] Selected server changed to ${selectedServer}, ensuring watch is established`);
      websocketRef.current.send(JSON.stringify({
        type: 'start_watching',
        server_id: selectedServer
      }));
    }
  }, [selectedServer]);

  // feat-294-filter-active-servers: load tenant.is_live once per user.
  useEffect(() => {
    if (!config.useDummyData && !user) return;
    let cancelled = false;
    const headers: Record<string, string> = user?.accessToken
      ? { Authorization: `Bearer ${user.accessToken}` }
      : {};
    fetchApi<any[]>('/api/tenant', { headers })
      .then(tenants => {
        if (cancelled || !Array.isArray(tenants)) return;
        setTenantIsLive(new Map(tenants.map(t => [String(t.tenant_id), !!t.is_live])));
      })
      .catch(err => {
        // Degrade: map stays empty → Demo mode hides everything; toggle off shows all.
        console.error('[RestaurantAdmin] Failed to load tenants for Demo mode:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // feat-080: one shared 1s tick drives every tile's count-up (cosmetic only).
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const clearAllSessions = () => {
    setSessions([{ id: 0, transcriptParts: [], interimTranscript: '' }]);
  };

  const handleServerSelect = (serverId: string) => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      if (selectedServer === serverId) {
        websocketRef.current.send(JSON.stringify({
          type: 'stop_watching',
          server_id: serverId
        }));
        console.debug('clearing2 selectedServer');
        setSelectedServer(null);
        setEvaluation([]);
        setIsFinalEvaluation(false);
        setSummary(null);
        setIsProcessing(false);
        clearAllSessions();
      } else {
        clearAllSessions();
        setEvaluation([]);
        setIsFinalEvaluation(false);
        setSummary(null);

        if (selectedServer) {
          websocketRef.current.send(JSON.stringify({
            type: 'stop_watching',
            server_id: selectedServer
          }));
        }

        websocketRef.current.send(JSON.stringify({
          type: 'start_watching',
          server_id: serverId
        }));
        console.debug('setting2 selectedServer', serverId);
        setSelectedServer(serverId);
      }
    }
  };

  const sendNotification = () => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && selectedServer) {
      websocketRef.current.send(JSON.stringify({
        type: "send_message",
        message: {
          type: "notification",
          title: "🔥 Nice you nailed it!",
          text: "💰 ✅  🥩✅  🗣️✅ ",
          positive: true
        }
      }));
    }
  };

  const sendCriteriaMissedFeedback = () => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && selectedServer) {
      websocketRef.current.send(JSON.stringify({
        type: "send_message",
        message: {
          type: "notification",
          title: "📝 Let's review the criteria",
          text: "💰➖  🥩➖  🗣️➖",
          positive: false
        }
      }));
    }
  };

  const sendBeerPromotion = () => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && selectedServer) {
      websocketRef.current.send(JSON.stringify({
        type: "send_message",
        message: {
          type: "notification",
          title: "🍹 🎉 🥂",
          text: "Drink promotion! (Beer)",
          positive: true
        }
      }));
    }
  };

  const forceEndShift = () => {
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && selectedServer) {
      const serverName = activeServers.find(s => s.id === selectedServer)?.name || 'this server';
      if (window.confirm(`Are you sure you want to forcefully end the shift for ${serverName}? This will stop recording and disconnect them.`)) {
        console.log(`Sending force_end_shift to server ${selectedServer}`);
        websocketRef.current.send(JSON.stringify({
          type: "send_message",
          message: {
            type: "force_end_shift"
          }
        }));
      }
    } else {
      console.error("Cannot force end shift: WebSocket not connected or no server selected.");
    }
  };

  const forceQuitAllActiveServers = async () => {
    const serverCount = activeServers.length;
    if (serverCount === 0) {
      alert("No active servers to quit.");
      return;
    }

    const serverNames = activeServers.map(s => s.name).join(", ");
    if (!window.confirm(`Are you sure you want to forcefully quit ALL ${serverCount} active server(s)?\n\nServers: ${serverNames}\n\nThis will stop recording and disconnect all of them.`)) {
      return;
    }

    try {
      const protocol = (config.environment === 'fake' || config.environment === 'local') ? 'http' : 'https';
      const apiUrl = `${protocol}://${RESTAURANT_BACKEND_HOST}/admin/force-quit-all-active-servers`;

      console.log(`Calling force-quit-all-active-servers endpoint: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log("Force quit result:", result);

      alert(`Successfully force quit ${result.servers_quit || 0} server(s).\n\nServers quit: ${result.servers_quit || 0}\nServers skipped: ${result.servers_skipped || 0}\nErrors: ${result.errors?.length || 0}`);

      // The websocket will receive server_update messages and update the UI automatically
    } catch (error) {
      console.error("Error force quitting all servers:", error);
      alert(`Failed to force quit servers: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const mergeConsecutiveSpeakers = (segments: TranscriptSegment[]): TranscriptSegment[] => {
    return segments.reduce((acc: TranscriptSegment[], curr) => {
      const lastSegment = acc[acc.length - 1];

      if (lastSegment && lastSegment.speaker === curr.speaker) {
        // Combine text of same speaker
        lastSegment.text += ' ' + curr.text;
        return acc;
      } else {
        // Different speaker or first segment
        return [...acc, { ...curr }];
      }
    }, []);
  };

  const visibleServers = useMemo(
    () => activeServers.filter(isVisible),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeServers, demoMode, tenantIsLive]
  );

  const sortedActiveServers = useMemo(() => {
    return [...visibleServers].sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleServers]);

  // feat-294-filter-active-servers: a selected server that Demo mode hides
  // (including the single-server auto-select) is deselected and unwatched.
  useEffect(() => {
    if (selectedServer && !visibleServers.some(s => s.id === selectedServer)) {
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({
          type: 'stop_watching',
          server_id: selectedServer
        }));
      }
      setSelectedServer(null);
      setEvaluation([]);
      setIsFinalEvaluation(false);
      setSummary(null);
      setIsProcessing(false);
      clearAllSessions();
    }
  }, [selectedServer, visibleServers]);

  return (
    <div className="min-h-screen bg-cream p-6 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl mb-2 font-display font-semibold text-ink">
            Restaurant Admin
          </h1>
          <div className="flex items-center gap-2 text-inkMuted">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Live Dashboard</span>
          </div>
        </div>

        {/* Active Servers */}
        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 className="text-xl font-display font-semibold text-ink">
              Active Servers
            </h2>
            <label className="flex items-center gap-2 text-sm text-inkMuted cursor-pointer select-none">
              <input
                type="checkbox"
                checked={demoMode}
                onChange={e => setDemoMode(e.target.checked)}
                className="h-4 w-4 accent-coral"
              />
              Hide live clients
            </label>
          </div>
          {visibleServers.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 border border-softBorder text-inkMuted text-sm">
              No active servers
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {sortedActiveServers.map(server => {
                const isSelected = selectedServer === server.id;
                return (
                  <button
                    key={server.id}
                    onClick={() => handleServerSelect(server.id)}
                    className={`rounded-xl p-4 text-left shadow-md relative overflow-hidden transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'bg-coral text-white ring-4 ring-coral/30'
                        : 'bg-coral text-white hover:opacity-90'
                    }`}
                  >
                    <div className="text-base font-bold leading-tight mb-1">
                      {server.name}
                    </div>
                    {server.location && (
                      <div className="text-xs opacity-90">
                        {server.location}
                      </div>
                    )}
                    {/* feat-080: live recording status. Absent recording_status
                        (older backend) → no chip, name+location only (AC7). */}
                    {server.recording_status && (
                      <div>
                        <ServerStatusChip
                          status={server.recording_status}
                          serverNowMs={nowMs - clockOffsetMs}
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Server Actions */}
        {selectedServer && (
          <section className="mb-8">
            <h2 className="mb-4 text-xl font-display font-semibold text-ink">
              Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                onClick={sendNotification}
                className="bg-[var(--green)] hover:opacity-90 text-white rounded-2xl px-6 py-4 flex items-center gap-3 transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
              >
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <SmilePlus className="w-5 h-5" />
                </div>
                <span className="font-semibold text-base">Send Positive Feedback</span>
              </button>

              <button
                onClick={sendCriteriaMissedFeedback}
                className="bg-gold hover:opacity-90 text-ink rounded-2xl px-6 py-4 flex items-center gap-3 transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
              >
                <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <span className="font-semibold text-base">All Criteria Missed</span>
              </button>

              <button
                onClick={sendBeerPromotion}
                className="bg-[var(--blue)] hover:opacity-90 text-white rounded-2xl px-6 py-4 flex items-center gap-3 transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
              >
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Beer className="w-5 h-5" />
                </div>
                <span className="font-semibold text-base">Send Beer Promotion</span>
              </button>

              <button
                onClick={forceEndShift}
                className="bg-coral hover:opacity-90 text-white rounded-2xl px-6 py-4 flex items-center gap-3 transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
              >
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <LogOut className="w-5 h-5" />
                </div>
                <span className="font-semibold text-base">Force End Shift</span>
              </button>
            </div>
          </section>
        )}

        {/* Global Actions */}
        {/* feat-294-filter-active-servers: hidden in Demo mode — the endpoint
            quits every server regardless of the filter, and N is the real total. */}
        {isAdmin && !demoMode && activeServers.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-4 text-xl font-display font-semibold text-ink">
              Global Actions
            </h2>
            <button
              onClick={forceQuitAllActiveServers}
              className="bg-[var(--destructive)] hover:opacity-90 text-white rounded-2xl px-6 py-4 flex items-center gap-3 transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
            >
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5" />
              </div>
              <span className="font-semibold text-base">
                Force Quit All Active Servers ({activeServers.length})
              </span>
            </button>
          </section>
        )}

        {selectedServer && (
          <>
            {/* Live Events */}
            <section className="mb-8">
              <h2 className="mb-4 text-xl font-display font-semibold text-ink">
                Live Events
              </h2>
              <div className="space-y-4">
                {sessions.map((session, index) => (
                  <div key={session.id}>
                    {session.notification && index !== 0 && session.notification.interaction_type === 'phrase_rule' && (
                      <div className={`mb-3 p-3 rounded-xl border ${
                        session.notification.polarity === 'negative'
                          ? 'bg-red-50 border-red-200 text-red-900'
                          : 'bg-green-50 border-green-200 text-green-900'
                      }`}>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              session.notification.polarity === 'negative'
                                ? 'bg-red-600 text-white'
                                : 'bg-green-600 text-white'
                            }`}
                          >
                            {session.notification.polarity === 'negative' ? '💡 Coaching moment' : '✨ Nice phrasing'}
                          </span>
                          {session.notification.phrase && (
                            <span className="text-xs font-mono text-ink">
                              "{session.notification.phrase}"
                            </span>
                          )}
                        </div>
                        {session.notification.matched_text && (
                          <div className="text-sm italic text-inkMuted mb-1">
                            Heard: "{session.notification.matched_text}"
                          </div>
                        )}
                        {session.notification.text && (
                          <div className="text-sm text-ink">
                            {session.notification.text}
                          </div>
                        )}
                        {session.notification.evidence && (
                          <div className="text-xs text-inkMuted mt-1">
                            {session.notification.evidence}
                          </div>
                        )}
                      </div>
                    )}
                    {session.notification && index !== 0 && session.notification.interaction_type !== 'phrase_rule' && (
                      <div className={`mb-3 p-3 rounded-xl ${session.notification.positive
                        ? 'bg-[var(--green-light)] text-green-800'
                        : 'bg-gold-light text-ink'
                        }`}>
                        <div className="flex items-center gap-3 flex-wrap">
                          {session.notification.text && (
                            <span className="text-sm font-mono">
                              {parseEmojiString(session.notification.text)}
                            </span>
                          )}
                          {session.notification.interaction_type && (
                            <span className="text-xs text-inkMuted capitalize">
                              {session.notification.interaction_type}
                            </span>
                          )}
                          {session.notification.fulfilled_metrics && session.notification.fulfilled_metrics.length > 0 && (
                            <span className="text-xs text-ink flex items-center gap-1 flex-wrap">
                              <span className="text-inkMuted">→</span>
                              {session.notification.fulfilled_metrics.map((m, i) => (
                                <span
                                  key={`${m.key}-${i}`}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/60 border border-softBorder"
                                >
                                  {m.emoji && <span>{m.emoji}</span>}
                                  <span className="font-medium">{m.label}</span>
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                        {session.notification.orderedItems && session.notification.orderedItems.length > 0 && (
                          <div className="mt-2">
                            <h4 className="font-semibold text-sm mb-1">Ordered Items:</h4>
                            <ul className="list-disc list-inside text-sm">
                              {session.notification.orderedItems.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {session.recordingEvent && (
                      <div className={`mb-3 p-3 rounded-xl ${session.recordingEvent.type === 'resume_recording'
                        ? 'bg-[var(--green-light)] text-green-800'
                        : 'bg-gold-light text-ink'
                        }`}>
                        <h3 className="font-semibold mb-1">
                          {session.recordingEvent.type === 'resume_recording' ? 'Recording Resumed' : 'Recording Paused'}
                        </h3>
                        {session.recordingEvent.userResumed && <p className="text-sm">Manually resumed by user</p>}
                        {session.recordingEvent.userPaused && <p className="text-sm">Manually paused by user</p>}
                        {session.recordingEvent.beaconName && (
                          <p className="text-sm">
                            Beacon: {session.recordingEvent.beaconName}
                            {session.recordingEvent.beaconPower !== undefined &&
                              ` (Signal: ${session.recordingEvent.beaconPower} dBm)`}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-softBorder min-h-[100px] max-h-[300px] overflow-y-auto">
                      {session.transcriptParts.length === 0 && !session.interimTranscript ? (
                        <div className="text-inkMuted text-sm">Waiting for transcript...</div>
                      ) : (
                        <div className="space-y-3">
                          {session.transcriptParts.map((segment, segIdx) => (
                            <div key={segIdx} className="flex items-start gap-3 pb-3 border-b border-softBorder last:border-b-0 last:pb-0">
                              <div className="w-2 h-2 rounded-full bg-coral mt-2 flex-shrink-0"></div>
                              <div className="flex-1 min-w-0">
                                <span className="font-semibold text-sm text-ink">[Speaker{segment.speaker}]: </span>
                                <span className="text-sm text-ink">{segment.text}</span>
                                {segIdx === session.transcriptParts.length - 1 && session.interimTranscript && (
                                  <span className="text-inkMuted italic text-sm"> {session.interimTranscript}</span>
                                )}
                              </div>
                            </div>
                          ))}
                          {session.transcriptParts.length === 0 && session.interimTranscript && (
                            <div className="text-inkMuted italic text-sm">{session.interimTranscript}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Task Checklist */}
            <section className="mb-8">
              <h2 className="mb-4 text-xl font-display font-semibold text-ink flex items-center">
                Required Tasks
                {isProcessing && (
                  <FaSpinner className="inline ml-2 animate-spin text-coral" />
                )}
              </h2>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-softBorder">
                {evaluation.length === 0 ? (
                  <p className="text-inkMuted text-sm">Waiting for evaluation...</p>
                ) : (
                  <ul className="space-y-3">
                    {evaluation.map((task, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-3 p-3 bg-cream rounded-xl"
                      >
                        {task.done ? (
                          <FaCheckCircle className="w-5 h-5 text-[var(--green)] flex-shrink-0" />
                        ) : isFinalEvaluation ? (
                          <FaTimesCircle className="w-5 h-5 text-coral flex-shrink-0" />
                        ) : (
                          <FaRegSquare className="w-5 h-5 text-inkMuted flex-shrink-0" />
                        )}
                        <span className="flex-grow text-sm text-ink">{task.task}</span>
                        <Tooltip id={`tooltip-${index}`} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* Performance Summary */}
            {isFinalEvaluation && summary && (
              <section className="mb-8">
                <h2 className="mb-4 text-xl font-display font-semibold text-ink">
                  Shift Performance Summary
                </h2>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-softBorder space-y-4">
                  {summary.well && summary.well.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-[var(--green)] mb-2">Done Well:</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {summary.well.map((item, index) => (
                          <li key={index} className="text-sm text-ink">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {summary.better && summary?.better?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-[var(--blue)] mb-2">Could Be Better:</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {summary.better.map((item, index) => (
                          <li key={index} className="text-sm text-ink">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Detailed Shift Analysis */}
            {isFinalEvaluation && detailedSummary && (
              <section className="mb-8">
                <h2 className="mb-4 text-xl font-display font-semibold text-ink">
                  Detailed Shift Analysis
                </h2>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-softBorder">
                  <div className="prose prose-sm max-w-none text-ink">
                    <ReactMarkdown>{detailedSummary}</ReactMarkdown>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RestaurantAdmin;